import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PanelLeftClose, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useAddCatalogPosition,
  useApplyRosterTemplate,
  useAttachRosterExpansion,
  useGetPositionsForIncident,
  useRosterTemplatesCatalog,
} from 'hooks/incident/useRoster';
import { useArchiveRosterTemplate } from 'hooks/rosterTemplateLibrary/rosterTemplateLibraryHooks';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import {
  orgTemplateIdFromSlug,
  partitionBySource,
  partitionRosterTemplates,
} from './templates/rosterTemplateCatalog';
import type {
  RosterTemplateCatalogEntry,
  RosterTemplateSidebarTab,
} from './templates/rosterTemplateTypes';
import { registerFocIncidentRosterTourApi } from 'components/MapPage/focIncidentRosterTour/focIncidentRosterTourBridge';
import { registerFocPlanningTourApi } from 'components/MapPage/focPlanningTour/focPlanningTourBridge';
import {
  isUscgFocRosterTemplateId,
  USCG_FOC_HURRICANE_SLUG,
} from 'components/MapPage/CorporateViewTabs/PlanLibraryTab/uscgFocPlanning';

/**
 * Library id for an org-saved template the user can delete; null for built-ins
 * and demo fixtures.
 */
function deletableOrgTemplateId(slug: string): string | null {
  const id = orgTemplateIdFromSlug(slug);
  return id && !isUscgFocRosterTemplateId(id) ? id : null;
}

const TAB_LABELS: Record<RosterTemplateSidebarTab, string> = {
  full: 'Templates',
  expansions: 'Template Expansions',
  positions: 'Positions',
};

/** Outline-friendly badges that stay readable on light and dark cards. */
const CATEGORY_BADGE: Record<string, string> = {
  minimal:
    'tw:border-slate-400 tw:bg-slate-500/15 tw:text-slate-700 tw:dark:text-slate-200',
  type1:
    'tw:border-red-400 tw:bg-red-500/15 tw:text-red-700 tw:dark:text-red-200',
  type2:
    'tw:border-orange-400 tw:bg-orange-500/15 tw:text-orange-800 tw:dark:text-orange-200',
  type3:
    'tw:border-amber-400 tw:bg-amber-500/15 tw:text-amber-800 tw:dark:text-amber-200',
  branch:
    'tw:border-emerald-400 tw:bg-emerald-500/15 tw:text-emerald-800 tw:dark:text-emerald-200',
  standard:
    'tw:border-sky-400 tw:bg-sky-500/15 tw:text-sky-800 tw:dark:text-sky-200',
  organization:
    'tw:border-violet-400 tw:bg-violet-500/15 tw:text-violet-800 tw:dark:text-violet-200',
};

export type RosterTemplateSidebarDraftHandlers = {
  occupiedNames: string[];
  selectedName: string | null;
  onLoad: (slug: string) => void;
  onAttach: (slug: string) => void;
  onAddPosition: (name: string) => void;
};

type RosterTemplateSidebarProps = {
  selectedPositionId: string | null;
  onCollapse?: () => void;
  /**
   * When set, Load / attach / add mutate the create-incident draft instead of
   * the live roster.
   */
  draft?: RosterTemplateSidebarDraftHandlers;
};

function TemplateCard({
  template,
  mode,
  selectedPositionId,
  busy,
  onLoad,
  onAttach,
  onDelete,
  loadTour,
}: {
  template: RosterTemplateCatalogEntry;
  mode: 'load' | 'attach';
  selectedPositionId: string | null;
  busy: boolean;
  onLoad: (slug: string) => void;
  onAttach: (slug: string) => void;
  onDelete?: () => void;
  loadTour?: string;
}) {
  const badgeClass =
    CATEGORY_BADGE[template.category] ?? CATEGORY_BADGE.standard;

  return (
    <div className="tw:rounded-md tw:border tw:border-border tw:p-3">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-2">
        <h3 className="tw:m-0 tw:text-sm tw:font-medium tw:leading-tight tw:text-foreground">
          {template.name}
        </h3>
        <Badge
          variant="outline"
          className={`tw:shrink-0 tw:text-[10px] ${badgeClass}`}
        >
          {template.category}
        </Badge>
      </div>
      {template.description ? (
        <p className="tw:mt-1.5 tw:mb-0 tw:text-[11px] tw:text-muted-foreground">
          {template.description}
        </p>
      ) : null}
      <p className="tw:mt-1 tw:mb-0 tw:text-[10px] tw:text-muted-foreground">
        {template.positionCount} position
        {template.positionCount === 1 ? '' : 's'}
      </p>
      <div className="tw:mt-2 tw:flex tw:items-center tw:justify-between tw:gap-2">
        {mode === 'load' ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="tw:h-7 tw:text-xs"
            disabled={busy}
            data-tour={loadTour}
            onClick={() => onLoad(template.slug)}
          >
            Load
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="tw:h-7 tw:text-xs"
            disabled={busy || !selectedPositionId}
            title={
              selectedPositionId
                ? 'Attach under the selected org-chart seat'
                : 'Select a seat on the chart first'
            }
            onClick={() => onAttach(template.slug)}
          >
            Add under selected
          </Button>
        )}
        {onDelete ? (
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="tw:size-7"
            disabled={busy}
            aria-label={`Delete ${template.name}`}
            title="Delete template"
            onClick={onDelete}
          >
            <Trash2 className="tw:size-3.5" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A titled run of template cards. Rendered once for the org's saved templates
 * and once for the built-in catalog, so it is obvious which ones the
 * organization owns.
 */
function TemplateGroup({
  title,
  emptyMessage,
  templates,
  mode,
  selectedPositionId,
  busy,
  onLoad,
  onAttach,
  onDelete,
}: {
  title: string;
  emptyMessage?: string;
  templates: RosterTemplateCatalogEntry[];
  mode: 'load' | 'attach';
  selectedPositionId: string | null;
  busy: boolean;
  onLoad: (slug: string) => void;
  onAttach: (slug: string) => void;
  /** Offered only on org-saved templates; built-ins never get it. */
  onDelete?: (template: RosterTemplateCatalogEntry) => void;
}) {
  if (templates.length === 0 && !emptyMessage) return null;

  return (
    <div className="tw:space-y-2">
      <p className="tw:m-0 tw:text-[10px] tw:font-semibold tw:uppercase tw:tracking-wide tw:text-muted-foreground">
        {title}
      </p>
      {templates.length === 0 ? (
        <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
          {emptyMessage}
        </p>
      ) : (
        templates.map((template) => (
          <TemplateCard
            key={template.slug}
            template={template}
            mode={mode}
            selectedPositionId={selectedPositionId}
            busy={busy}
            onLoad={onLoad}
            onAttach={onAttach}
            onDelete={
              onDelete && deletableOrgTemplateId(template.slug)
                ? () => onDelete(template)
                : undefined
            }
            loadTour={
              mode === 'load' && template.slug === USCG_FOC_HURRICANE_SLUG
                ? 'foc-roster-load'
                : undefined
            }
          />
        ))
      )}
    </div>
  );
}

export default function RosterTemplateSidebar({
  selectedPositionId,
  onCollapse,
  draft,
}: RosterTemplateSidebarProps) {
  const [tab, setTab] = useState<RosterTemplateSidebarTab>('full');
  const [catalogFilter, setCatalogFilter] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [pendingLoad, setPendingLoad] =
    useState<RosterTemplateCatalogEntry | null>(null);
  const [pendingDelete, setPendingDelete] =
    useState<RosterTemplateCatalogEntry | null>(null);
  const archiveTemplate = useArchiveRosterTemplate();

  const catalogQuery = useRosterTemplatesCatalog();
  const positionsQuery = useGetPositionsForIncident();
  const applyTemplate = useApplyRosterTemplate();
  const attachExpansion = useAttachRosterExpansion();
  const addPosition = useAddCatalogPosition();

  const onChartNames = useMemo(
    () =>
      new Set(
        draft
          ? draft.occupiedNames
          : (positionsQuery.data ?? []).map((p) => p.name)
      ),
    [draft, positionsQuery.data]
  );

  const { templates, expansions } = useMemo(
    () => partitionRosterTemplates(catalogQuery.data?.templates ?? []),
    [catalogQuery.data?.templates]
  );
  const templatesBySource = useMemo(
    () => partitionBySource(templates),
    [templates]
  );

  useEffect(() => {
    const previewRosterLoad = () => {
      setTab('full');
      const template = templatesBySource.organization.find(
        (entry) => entry.slug === USCG_FOC_HURRICANE_SLUG
      );
      if (template) setPendingLoad(template);
    };
    const unregisterPlanning = registerFocPlanningTourApi({
      previewRosterLoad,
    });
    const unregisterRoster = registerFocIncidentRosterTourApi({
      previewRosterLoad,
    });
    return () => {
      unregisterPlanning();
      unregisterRoster();
    };
  }, [templatesBySource.organization]);
  const expansionsBySource = useMemo(
    () => partitionBySource(expansions),
    [expansions]
  );

  const filteredCatalog = useMemo(() => {
    const query = catalogFilter.trim().toLowerCase();
    return (catalogQuery.data?.addablePositionTitles ?? []).filter((title) => {
      if (onChartNames.has(title)) return false;
      if (!query) return true;
      return title.toLowerCase().includes(query);
    });
  }, [catalogFilter, catalogQuery.data?.addablePositionTitles, onChartNames]);

  const selectedName =
    draft?.selectedName ??
    positionsQuery.data?.find((p) => p.id === selectedPositionId)?.name ??
    null;

  const busy =
    applyTemplate.isPending ||
    attachExpansion.isPending ||
    addPosition.isPending;

  const requestLoad = (template: RosterTemplateCatalogEntry) => {
    if (draft) {
      draft.onLoad(template.slug);
      return;
    }
    // Add and replace do the same thing on an empty chart, so skip the choice.
    if (!positionsQuery.isLoading && (positionsQuery.data ?? []).length === 0) {
      applyTemplate.mutate({ slug: template.slug, mode: 'add' });
      return;
    }
    setPendingLoad(template);
  };

  const handleAttach = (slug: string) => {
    if (!selectedPositionId) {
      toast.info('Select a seat on the org chart first');
      return;
    }
    if (draft) {
      draft.onAttach(slug);
      return;
    }
    attachExpansion.mutate({ slug, parentPositionId: selectedPositionId });
  };

  const handleAddTitle = (name: string) => {
    if (!selectedPositionId) {
      toast.info('Select a seat on the org chart first');
      return;
    }
    if (draft) {
      draft.onAddPosition(name);
      return;
    }
    addPosition.mutate({ name, parentPositionId: selectedPositionId });
  };

  return (
    <aside
      className="dark tw:flex tw:h-full tw:min-h-0 tw:w-[300px] tw:shrink-0 tw:flex-col tw:overflow-hidden tw:rounded-md tw:border tw:border-border tw:bg-background"
      data-testid="roster-template-sidebar"
    >
      <div className="tw:flex tw:shrink-0 tw:items-center tw:justify-between tw:gap-1 tw:border-b tw:border-border tw:px-1">
        <nav className="tw:flex tw:min-w-0 tw:flex-1">
          {(Object.keys(TAB_LABELS) as RosterTemplateSidebarTab[]).map(
            (entry) => (
              <button
                key={entry}
                type="button"
                className={`tw:flex-1 tw:px-1.5 tw:py-2 tw:text-[10px] tw:font-medium tw:leading-tight tw:transition-colors sm:tw:text-[11px] ${
                  tab === entry
                    ? 'tw:border-b-2 tw:border-sky-500 tw:text-foreground'
                    : 'tw:text-muted-foreground tw:hover:text-foreground'
                }`}
                onClick={() => setTab(entry)}
              >
                {TAB_LABELS[entry]}
              </button>
            )
          )}
        </nav>
        {onCollapse ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="tw:h-7 tw:w-7 tw:shrink-0"
            aria-label="Hide templates panel"
            title="Hide templates panel"
            onClick={onCollapse}
          >
            <PanelLeftClose className="tw:h-4 tw:w-4" />
          </Button>
        ) : null}
      </div>

      <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:overscroll-contain tw:p-3">
        {catalogQuery.isLoading ? (
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            Loading catalog…
          </p>
        ) : null}

        {tab === 'full' ? (
          <div className="tw:space-y-4">
            <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
              Load a template to either add its missing seats to the current org
              chart, or replace all seats with the template.
            </p>
            <TemplateGroup
              title="Organization"
              emptyMessage="No saved templates yet. Save the current chart from the roster header to start your library."
              templates={templatesBySource.organization}
              mode="load"
              selectedPositionId={selectedPositionId}
              busy={busy}
              onLoad={(slug) => {
                const template = templatesBySource.organization.find(
                  (entry) => entry.slug === slug
                );
                if (template) requestLoad(template);
              }}
              onAttach={handleAttach}
              onDelete={setPendingDelete}
            />
            <TemplateGroup
              title="Built-in"
              templates={templatesBySource.builtIn}
              mode="load"
              selectedPositionId={selectedPositionId}
              busy={busy}
              onLoad={(slug) => {
                const template = templatesBySource.builtIn.find(
                  (entry) => entry.slug === slug
                );
                if (template) requestLoad(template);
              }}
              onAttach={handleAttach}
            />
          </div>
        ) : null}

        {tab === 'expansions' ? (
          <div className="tw:space-y-4">
            <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
              {selectedName
                ? `Selected: ${selectedName}`
                : 'Select a seat on the chart, then attach an expansion under it.'}
            </p>
            <TemplateGroup
              title="Organization"
              emptyMessage="No saved expansions yet. Save the current chart as an expansion from the roster header."
              templates={expansionsBySource.organization}
              mode="attach"
              selectedPositionId={selectedPositionId}
              busy={busy}
              onLoad={() => undefined}
              onAttach={handleAttach}
              onDelete={setPendingDelete}
            />
            <TemplateGroup
              title="Built-in"
              templates={expansionsBySource.builtIn}
              mode="attach"
              selectedPositionId={selectedPositionId}
              busy={busy}
              onLoad={() => undefined}
              onAttach={handleAttach}
            />
          </div>
        ) : null}

        {tab === 'positions' ? (
          <div className="tw:space-y-3">
            <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
              {selectedName
                ? `Add under: ${selectedName}`
                : 'Select a seat on the chart to add a position under it.'}
            </p>
            <Input
              value={catalogFilter}
              onChange={(event) => setCatalogFilter(event.target.value)}
              placeholder="Filter catalog…"
              className="tw:h-8 tw:text-xs"
            />
            <div className="tw:max-h-64 tw:space-y-1 tw:overflow-y-auto">
              {filteredCatalog.map((title) => (
                <button
                  key={title}
                  type="button"
                  disabled={busy || !selectedPositionId}
                  className="tw:flex tw:w-full tw:items-center tw:justify-between tw:rounded tw:px-2 tw:py-1.5 tw:text-left tw:text-xs tw:text-foreground tw:hover:bg-muted disabled:tw:opacity-40"
                  onClick={() => handleAddTitle(title)}
                >
                  <span>{title}</span>
                  <span className="tw:text-[10px] tw:text-sky-600 tw:dark:text-sky-400">
                    Add
                  </span>
                </button>
              ))}
              {filteredCatalog.length === 0 ? (
                <p className="tw:m-0 tw:text-[11px] tw:text-muted-foreground">
                  No matching titles left to add.
                </p>
              ) : null}
            </div>
            <div className="tw:border-t tw:border-border tw:pt-3">
              <p className="tw:mb-2 tw:text-[11px] tw:font-medium tw:text-foreground">
                Custom position
              </p>
              <div className="tw:flex tw:gap-2">
                <Input
                  value={customTitle}
                  onChange={(event) => setCustomTitle(event.target.value)}
                  placeholder="Title"
                  className="tw:h-8 tw:text-xs"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="tw:h-8 tw:shrink-0 tw:text-xs"
                  disabled={busy || !selectedPositionId || !customTitle.trim()}
                  onClick={() => {
                    handleAddTitle(customTitle.trim());
                    setCustomTitle('');
                  }}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <AlertDialog
        open={!!pendingLoad}
        onOpenChange={(open) => {
          if (!open) setPendingLoad(null);
        }}
      >
        <AlertDialogContent className="dark">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Load {pendingLoad ? `“${pendingLoad.name}”` : 'template'}?
            </AlertDialogTitle>
            <AlertDialogDescription render={<div className="tw:space-y-3" />}>
              <p>Choose how to apply this template to the org chart.</p>
              <ul className="tw:m-0 tw:list-disc tw:space-y-2 tw:ps-4">
                <li>
                  <strong>Add to existing</strong> — create only seats that are
                  not already on the chart. Current seats stay in place.
                </li>
                <li>
                  <strong>Replace all seats</strong> — remove every current
                  seat, then create the template tree. Assignments and seat
                  resources on removed seats are cleared.
                </li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy || !pendingLoad}
              onClick={() => {
                if (!pendingLoad) return;
                applyTemplate.mutate({ slug: pendingLoad.slug, mode: 'add' });
                setPendingLoad(null);
              }}
            >
              Add to existing
            </AlertDialogAction>
            <AlertDialogAction
              variant="destructive"
              disabled={busy || !pendingLoad}
              onClick={() => {
                if (!pendingLoad) return;
                applyTemplate.mutate({
                  slug: pendingLoad.slug,
                  mode: 'replace',
                });
                setPendingLoad(null);
              }}
            >
              Replace all seats
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        title="Delete roster template"
        description={
          pendingDelete
            ? `Delete “${pendingDelete.name}”? It is removed from the Templates panel for everyone in this organization. Org charts already built from it keep their seats.`
            : ''
        }
        pending={archiveTemplate.isPending}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        onConfirm={() => {
          const id = pendingDelete
            ? deletableOrgTemplateId(pendingDelete.slug)
            : null;
          if (!id) return;
          archiveTemplate.mutate(id, {
            onSuccess: () => setPendingDelete(null),
          });
        }}
      />
    </aside>
  );
}
